/**
 * Settings ▸ Model policy: copy a policy as text on one server and paste it
 * on another, so prod runs exactly the models tested on the laptop.
 *
 * Copy puts the policy's rules on the clipboard, with the models each rule
 * matches on this server. Paste shows, rule by rule, whether the other server
 * would run the same models BEFORE anything is written, and then makes a
 * DRAFT: the live policy only changes when that draft is promoted.
 */
import { useState } from 'react';
import api from '../../services/api';
import type { CopiedPolicy, PastePreview, PipelineVersion } from '../../types';
import { pasteSummary, pasteWouldWrite, readPolicyText, runsTheSame } from '../../pipeline/policyTransfer';

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

const STATUS_STYLE: Record<string, string> = {
  same: 'text-emerald-700 dark:text-emerald-300',
  differs: 'text-amber-700 dark:text-amber-300',
  missing: 'text-red-700 dark:text-red-300',
  invalid: 'text-gray-500 dark:text-gray-400',
};

const STATUS_WORDS: Record<string, string> = {
  same: 'same',
  differs: 'different models',
  missing: 'no model here',
  invalid: 'skipped',
};

/** Copies one policy to the clipboard. Where the browser refuses (a page not
 *  served over https), the text is shown to copy by hand instead. */
export function CopyPolicyButton({ version }: { version: PipelineVersion }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [fallback, setFallback] = useState<string | null>(null);

  const copy = async () => {
    setBusy(true);
    setNote(null);
    setFallback(null);
    try {
      const data = await api.exportPipelineVersion(version.id);
      const text = JSON.stringify(data, null, 1);
      try {
        await navigator.clipboard.writeText(text);
        setNote(`Copied ${data.rules.length} rule(s). Paste them on the other server.`);
      } catch {
        setFallback(text);
      }
    } catch (e) {
      setNote(errText(e, 'Could not copy the policy'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={copy}
        disabled={busy}
        data-testid="copy-policy"
        title={`Copy ${version.name} as text, to paste on another server`}
        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        Copy as text
      </button>
      {note && (
        <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="copy-policy-note">
          {note}
        </span>
      )}
      {fallback && (
        <div className="basis-full mt-2" data-testid="copy-policy-fallback">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            The browser would not copy it. Select this text and copy it by hand:
          </p>
          <textarea
            readOnly
            value={fallback}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full h-32 font-mono text-xs p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
          />
        </div>
      )}
    </>
  );
}

/** Paste a copied policy, see what it would run here, and make a draft of it. */
export function PastePolicy({ onPasted }: { onPasted: (draftId: number) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [policy, setPolicy] = useState<CopiedPolicy | null>(null);
  const [preview, setPreview] = useState<PastePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setText('');
    setPolicy(null);
    setPreview(null);
    setError(null);
  };

  const check = async () => {
    setError(null);
    setPreview(null);
    const read = readPolicyText(text);
    if ('error' in read) {
      setError(read.error);
      return;
    }
    setBusy(true);
    try {
      setPolicy(read.policy);
      setPreview(await api.previewPastedPolicy(read.policy));
    } catch (e) {
      setError(errText(e, 'Could not read the pasted policy'));
    } finally {
      setBusy(false);
    }
  };

  const makeDraft = async () => {
    if (!policy) return;
    setBusy(true);
    setError(null);
    try {
      const draft = await api.importPastedPolicy(policy);
      reset();
      setOpen(false);
      onPasted(draft.id);
    } catch (e) {
      setError(errText(e, 'Could not make the draft'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="paste-policy"
        className="px-2 py-1.5 text-xs border border-dashed border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300"
      >
        + Paste a policy…
      </button>
    );
  }

  return (
    <div className="basis-full mt-2 p-3 border rounded-lg bg-white dark:bg-gray-800 text-sm" data-testid="paste-policy-panel">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Paste a policy copied on another server with Copy as text. You will see whether this server runs the same models
        for each rule before anything is made; then it becomes a new policy to try on your account and make live.
      </p>
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setPreview(null);
        }}
        placeholder='{"format": "model-policy/1", ...}'
        data-testid="paste-policy-text"
        className="w-full h-28 font-mono text-xs p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
      />
      <div className="mt-2 flex gap-2">
        <button
          onClick={check}
          disabled={busy}
          data-testid="paste-policy-check"
          className="px-3 py-1.5 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          Check it
        </button>
        <button
          onClick={() => {
            reset();
            setOpen(false);
          }}
          disabled={busy}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded"
        >
          Cancel
        </button>
      </div>

      {error && <p className="mt-2 text-red-600 dark:text-red-400">{error}</p>}

      {preview && (
        <div className="mt-3" data-testid="paste-policy-preview">
          <p className="font-medium text-gray-800 dark:text-gray-100">
            {preview.name ? `${preview.name}: ` : ''}
            {pasteSummary(preview.counts)}
          </p>
          <p
            className={`text-xs mt-1 ${
              runsTheSame(preview.counts)
                ? 'text-emerald-700 dark:text-emerald-300'
                : 'text-amber-700 dark:text-amber-300'
            }`}
            data-testid="paste-policy-verdict"
          >
            {runsTheSame(preview.counts)
              ? 'Every rule runs exactly the models it ran where it was copied.'
              : 'Some rules would run different models here, or none. Add or enable those models here first if you need them to match.'}
          </p>
          <div className="mt-2 max-h-72 overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="font-normal py-1">job</th>
                  <th className="font-normal py-1">rule</th>
                  <th className="font-normal py-1">there</th>
                  <th className="font-normal py-1">here</th>
                  <th className="font-normal py-1 pl-3">result</th>
                </tr>
              </thead>
              <tbody>
                {preview.rules.map((r) => (
                  <tr key={r.index} className="border-t border-gray-200 dark:border-gray-700 align-top">
                    <td className="py-1 font-mono">
                      {r.stage ?? '-'}
                      {r.archetype ? ` · ${r.archetype}` : ''}
                    </td>
                    <td className="py-1 font-mono">
                      {r.model_pattern ?? '-'}
                      {!r.enabled && <span className="ml-1 text-gray-400">(off)</span>}
                    </td>
                    <td className="py-1 font-mono">{r.there.join(', ')}</td>
                    <td className="py-1 font-mono">{r.here.join(', ')}</td>
                    <td className={`py-1 pl-3 ${STATUS_STYLE[r.status] ?? ''}`}>
                      {r.reason ?? STATUS_WORDS[r.status] ?? r.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <button
              onClick={makeDraft}
              disabled={busy || !pasteWouldWrite(preview.counts)}
              data-testid="paste-policy-draft"
              className="px-3 py-1.5 bg-green-600 text-white rounded disabled:opacity-50"
            >
              Make a new policy from it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
